/**
 * Storage Wrapper
 * -----------------------------------------------------------------------------
 * Implements IAsyncStorage interface for ECS client using localStorage.
 * Provides error handling and logging for storage operations.
 */

import type { IAsyncStorage } from "@1js/ecs-client";
import { logDebug, logWarn } from "./logger";

/**
 * LocalStorage wrapper that implements IAsyncStorage interface.
 * Provides async interface over synchronous localStorage operations.
 */
export class LocalStorageWrapper implements IAsyncStorage {
  /**
   * Store a value in localStorage.
   */
  async setItem(key: string, value: string): Promise<void> {
    try {
      globalThis.localStorage.setItem(key, value);
      logDebug(`Storage: Set item ${key} (${value.length} chars)`, 'STORAGE');
    } catch (error) {
      logWarn(`Storage: Failed to set item ${key}`, 'STORAGE', error);
      throw error;
    }
  }

  /**
   * Retrieve a value from localStorage.
   */
  async getItem(key: string): Promise<string | null> {
    try {
      const value = globalThis.localStorage.getItem(key);
      if (value !== null) {
        logDebug(`Storage: Retrieved item ${key} (${value.length} chars)`, 'STORAGE');
      } else {
        logDebug(`Storage: Item ${key} not found`, 'STORAGE');
      }
      return value;
    } catch (error) {
      logWarn(`Storage: Failed to get item ${key}`, 'STORAGE', error);
      throw error;
    }
  }

  /**
   * Remove a value from localStorage.
   */
  async removeItem(key: string): Promise<void> {
    try {
      globalThis.localStorage.removeItem(key);
      logDebug(`Storage: Removed item ${key}`, 'STORAGE');
    } catch (error) {
      logWarn(`Storage: Failed to remove item ${key}`, 'STORAGE', error);
      throw error;
    }
  }

  /**
   * Clear all items from localStorage (use with caution).
   */
  async clear(): Promise<void> {
    try {
      globalThis.localStorage.clear();
      logDebug('Storage: Cleared all items', 'STORAGE');
    } catch (error) {
      logWarn('Storage: Failed to clear storage', 'STORAGE', error);
      throw error;
    }
  }

  /**
   * Get the number of items in localStorage.
   */
  async length(): Promise<number> {
    try {
      const length = globalThis.localStorage.length;
      logDebug(`Storage: ${length} items in storage`, 'STORAGE');
      return length;
    } catch (error) {
      logWarn('Storage: Failed to get storage length', 'STORAGE', error);
      throw error;
    }
  }

  /**
   * Get all keys in localStorage.
   */
  async getAllKeys(): Promise<string[]> {
    try {
      const keys: string[] = [];
      for (let i = 0; i < globalThis.localStorage.length; i++) {
        const key = globalThis.localStorage.key(i);
        if (key) keys.push(key);
      }
      logDebug(`Storage: Retrieved ${keys.length} keys`, 'STORAGE');
      return keys;
    } catch (error) {
      logWarn('Storage: Failed to get all keys', 'STORAGE', error);
      throw error;
    }
  }

  /**
   * Check if localStorage is available.
   */
  static isAvailable(): boolean {
    try {
      const testKey = '__ecs_storage_test__';
      globalThis.localStorage.setItem(testKey, 'test');
      globalThis.localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get storage usage information.
   */
  async getStorageInfo(): Promise<{
    available: boolean;
    itemCount: number;
    estimatedSize: number;
  }> {
    try {
      const available = LocalStorageWrapper.isAvailable();
      if (!available) {
        return { available: false, itemCount: 0, estimatedSize: 0 };
      }

      const itemCount = await this.length();
      let estimatedSize = 0;

      // Estimate total size by checking all items
      for (let i = 0; i < globalThis.localStorage.length; i++) {
        const key = globalThis.localStorage.key(i);
        if (key) {
          const value = globalThis.localStorage.getItem(key);
          if (value) {
            estimatedSize += key.length + value.length;
          }
        }
      }

      return { available, itemCount, estimatedSize };
    } catch (error) {
      logWarn('Storage: Failed to get storage info', 'STORAGE', error);
      return { available: false, itemCount: 0, estimatedSize: 0 };
    }
  }

  /**
   * Clear ECS-related items only.
   */
  async clearEcsItems(): Promise<void> {
    try {
      const keys = await this.getAllKeys();
      const ecsKeys = keys.filter(key => key.includes('ecs') || key.includes('ECS'));
      
      for (const key of ecsKeys) {
        await this.removeItem(key);
      }
      
      logDebug(`Storage: Cleared ${ecsKeys.length} ECS-related items`, 'STORAGE');
    } catch (error) {
      logWarn('Storage: Failed to clear ECS items', 'STORAGE', error);
      throw error;
    }
  }
}